// src/components/Header.jsx
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FaUserCircle } from "react-icons/fa";
import logoImg from "../assets/M2_Lab_Logo.png";
import "./Header.css";
import BASE_URL from "../config";

export default function Header({
  links = [
    { label: "Home", to: "/" },
    { label: "MLLMs", to: "/vlm" },          // ✅ added here
    { label: "Newsroom", to: "/newsroom" },
    { label: "Interviews", to: "/interview" },
    { label: "Statistics", to: "/statistics" },
    { label: "Team", to: "/team" },
    { label: "Feedback", to: "/feedback" },
  ],
  sticky = true,
}) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState(null);

  // Check for active session
  useEffect(() => {
    async function checkSession() {
      try {
        const res = await fetch(`${BASE_URL}/verify-session`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          const name =
            data.first_name ||
            (data.email ? data.email.split("@")[0] : null) ||
            localStorage.getItem("userEmail");

          if (name) {
            setUserName(name);
            localStorage.setItem("userName", name);
          }
        } else {
          handleLogout(false); // don’t redirect if background check fails
        }
      } catch {
        handleLogout(false);
      }
    }

    checkSession();
    const interval = setInterval(checkSession, 5 * 60 * 1000); // re-check every 5 min
    return () => clearInterval(interval);
  }, []);

  const handleLogout = (redirect = true) => {
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    setUserName(null);
    if (redirect) navigate("/login");
  };

  return (
    <header
      className={`m2-header ${sticky ? "m2-header--sticky" : ""}`}
      role="banner"
    >
      {/* Gradient + texture layers */}
      <div className="m2-header__bg" aria-hidden="true" />

      {/* Connecting-dots SVG overlay */}
      <svg
        className="m2-header__network"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <g className="m2-network__lines" strokeWidth="0.25">
          <line x1="5" y1="30" x2="18" y2="18" />
          <line x1="18" y1="18" x2="35" y2="22" />
          <line x1="35" y1="22" x2="52" y2="15" />
          <line x1="52" y1="15" x2="70" y2="22" />
          <line x1="70" y1="22" x2="84" y2="12" />
          <line x1="18" y1="18" x2="12" y2="40" />
          <line x1="35" y1="22" x2="28" y2="40" />
          <line x1="52" y1="15" x2="48" y2="36" />
          <line x1="70" y1="22" x2="62" y2="38" />
          <line x1="84" y1="12" x2="92" y2="28" />
          <line x1="62" y1="38" x2="80" y2="42" />
          <line x1="28" y1="40" x2="44" y2="50" />
          <line x1="44" y1="50" x2="64" y2="54" />
          <line x1="64" y1="54" x2="86" y2="60" />
        </g>
        <g className="m2-network__nodes">
          <circle cx="5" cy="30" r="1.2" />
          <circle cx="12" cy="40" r="1.1" />
          <circle cx="18" cy="18" r="1.3" />
          <circle cx="28" cy="40" r="1.2" />
          <circle cx="35" cy="22" r="1.1" />
          <circle cx="44" cy="50" r="1.1" />
          <circle cx="48" cy="36" r="1.1" />
          <circle cx="52" cy="15" r="1.2" />
          <circle cx="62" cy="38" r="1.2" />
          <circle cx="64" cy="54" r="1.1" />
          <circle cx="70" cy="22" r="1.2" />
          <circle cx="80" cy="42" r="1.1" />
          <circle cx="84" cy="12" r="1.2" />
          <circle cx="86" cy="60" r="1.2" />
          <circle cx="92" cy="28" r="1.1" />
        </g>
      </svg>

      <div className="m2-header__inner">
        {/* Brand */}
        <button
          className="m2-brand"
          onClick={() => navigate("/")}
          aria-label="Go to home"
        >
          <img src={logoImg} alt="M2 Lab Logo" className="m2-brand__logo" />
          <span className="m2-brand__title">Purdue M2 Lab</span>
        </button>

        {/* Nav */}
        <nav className="m2-nav" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `m2-nav__link ${isActive ? "is-active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        {userName ? (
          <button
            className="m2-login"
            onClick={() => handleLogout(true)}
            title="Logout"
            aria-label="Logout"
          >
            <FaUserCircle className="m2-login__icon" />
            <span className="m2-login__label">{userName} (Logout)</span>
          </button>
        ) : (
          <button
            className="m2-login"
            onClick={() => navigate("/login")}
            title="Sign in"
            aria-label="Sign in"
          >
            <FaUserCircle className="m2-login__icon" />
            <span className="m2-login__label">Sign in</span>
          </button>
        )}
      </div>
    </header>
  );
}
